import React, { useEffect, useRef, useState } from 'react';

/**
 * HtmlPreview — renders HTML source inside a sandboxed iframe for the "Rich" view.
 *
 * Security: sandbox="allow-scripts allow-popups" (no allow-same-origin) runs scripts
 * in an opaque origin with no access to parent cookies/DOM. Matches the pattern
 * used by Claude artifacts, ChatGPT Canvas, and v0.dev.
 *
 * Sizing: the iframe auto-grows to fit its content (up to a max) so a small HTML
 * snippet inside a chat message doesn't take 600px of vertical space. We measure
 * the iframe body height via postMessage-free contentWindow access — but because
 * the iframe is in an opaque origin, we can't actually read its body. So instead
 * we inject a tiny self-measuring script and listen for a postMessage from it.
 */
const HtmlPreview = ({ content, minHeight = 120, maxHeight = 600 }) => {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(minHeight);

  // Inject a <script> into the srcDoc that posts the document height to the
  // parent. Because the sandbox omits allow-same-origin, only postMessage can
  // cross the boundary — that's exactly what we use.
  const srcDocWithSizer = `${content}
<script>
  (function(){
    function sendSize(){
      try {
        var h = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight
        );
        parent.postMessage({ __richCodeBlockResize: true, height: h }, '*');
      } catch (e) { /* ignore */ }
    }
    window.addEventListener('load', sendSize);
    window.addEventListener('resize', sendSize);
    // Re-measure after a tick so web fonts / async scripts settle
    setTimeout(sendSize, 50);
    setTimeout(sendSize, 250);
    setTimeout(sendSize, 800);
  })();
<\/script>`;

  useEffect(() => {
    const onMessage = (event) => {
      if (!event.data || event.data.__richCodeBlockResize !== true) return;
      // We can't verify event.source strictly because the iframe is in an opaque
      // origin; however we check the iframe ref's contentWindow to scope it.
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return;
      }
      const reported = Number(event.data.height) || 0;
      const clamped = Math.max(minHeight, Math.min(maxHeight, reported + 8));
      setHeight(clamped);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [minHeight, maxHeight]);

  return (
    <iframe
      ref={iframeRef}
      title="HTML preview"
      srcDoc={srcDocWithSizer}
      sandbox="allow-scripts allow-popups"
      className="rich-code-block-html-iframe"
      style={{ height: `${height}px` }}
      loading="eager"
    />
  );
};

export default HtmlPreview;
